import { Checkbox, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import ExtendHeaderTooltipBase from "./ExtendHeaderTooltip";
import ExtendLocationFormBase from "./ExtendLocationForm";
import { useFormikContext } from "formik";
import RpVirtualListBase from "@/components/virtual/RpVirtualList";
import ExtendLocationBulkSelectionBase from "./extend-location-bulk-selection/ExtendLocationBulkSelection";
import RpAlertBase from "@/components/alerts/RpAlert";
import {
  EstimatedImpressionsDisclosure,
  EstimatedImpressionsVisibilityProvider,
} from "@/features/campaigns/shared/components/EstimatedImpressions";
import { canShowImpressionEstimation } from "@/features/campaigns/shared/helpers/estimatedImpressionsHelper";

type LocationId = number | string;

type ExtendLocation = {
  location_id: LocationId;
  [key: string]: unknown;
};

type ExtendLocationValues = {
  selectedLoc?: Record<LocationId, unknown>;
};

type ExtendLocationsProps = {
  locationData: ExtendLocation[];
  campaignData?: Record<string, unknown>;
};

const ExtendHeaderTooltip = ExtendHeaderTooltipBase as React.ComponentType<
  Record<string, unknown>
>;

const ExtendLocationForm =
  ExtendLocationFormBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const RpVirtualList = RpVirtualListBase as unknown as React.ComponentType<
  Record<string, unknown>
>;

const ExtendLocationBulkSelection =
  ExtendLocationBulkSelectionBase as unknown as React.ComponentType<
    Record<string, unknown>
  >;

const RpAlert = RpAlertBase as React.ComponentType<
  Record<string, unknown> & { children?: React.ReactNode }
>;

function ExtendLocations({ locationData, campaignData }: ExtendLocationsProps) {
  const { values, setValues } = useFormikContext<ExtendLocationValues>();

  const [showLocationCheckBox, setShowLocationCheckBox] = useState(false);
  const [checkedLocations, setCheckedLocations] = useState<LocationId[]>([]);
  const [disableLocations, setDisableLocations] = useState<LocationId[]>([]);
  const impressionEstimationEnabled = canShowImpressionEstimation(campaignData);

  const handleToggleOpenBulkSelection = useCallback((isOpen = true) => {
    setShowLocationCheckBox(isOpen);
    if (!isOpen) {
      setCheckedLocations([]);
    }
  }, []);

  const handleSetDisableLocations = useCallback((locations: LocationId[]) => {
    setDisableLocations(locations);
    // remove disabled locations from checked locations
    setCheckedLocations((prev) => prev.filter((id) => !locations.includes(id)));
  }, []);

  return (
    <EstimatedImpressionsVisibilityProvider>
      <div className="mb-3">
        {locationData?.length > 1 && (
          <ExtendLocationBulkSelection
            onToggleBulkSelection={handleToggleOpenBulkSelection}
            campaignData={campaignData}
            checkedLocations={checkedLocations}
            setLocationValues={setValues}
            setDisableLocations={handleSetDisableLocations}
          />
        )}
        <EstimatedImpressionsDisclosure
          enabled={impressionEstimationEnabled}
          variant="standalone"
        />
        <div
          className={`joined-campaigns ${impressionEstimationEnabled ? "pt-3" : "pt-4"}`}
        >
          {showLocationCheckBox && (
            <div className="d-flex align-items-center ml-3 mb-2 d-lg-none">
              <Checkbox
                color="primary"
                checked={
                  disableLocations.length > 0 &&
                  disableLocations.length < locationData.length
                    ? checkedLocations.length ===
                      locationData.length - disableLocations.length
                    : checkedLocations.length === locationData.length
                }
                onChange={(e) => {
                  if (e.target.checked) {
                    setCheckedLocations((_prev) =>
                      locationData
                        .map((loc) => loc.location_id)
                        .filter((id) => !disableLocations.includes(id))
                    );
                  } else {
                    setCheckedLocations([]);
                  }
                }}
                className="m-0 p-0"
              />
              <Typography variant="body2" className="ml-2">
                Select All
              </Typography>
            </div>
          )}
          <div className="row align-items-stretch">
            <div className="col-12 d-none d-lg-block">
              <div
                className="card border-0 heading-card"
                data-testid="extend-locations-table-heading"
              >
                <div className="card-body py-3 px-3">
                  <div className="row align-items-start g-0 ">
                    {showLocationCheckBox && (
                      <div
                        className={`col-1 col-lg-1 align-items-center justify-content-center ${showLocationCheckBox ? "d-flex" : "d-none"}`}
                      >
                        <Checkbox
                          color="primary"
                          checked={
                            disableLocations.length > 0 &&
                            disableLocations.length < locationData.length
                              ? checkedLocations.length ===
                                locationData.length - disableLocations.length
                              : checkedLocations.length === locationData.length
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setCheckedLocations((_prev) =>
                                locationData
                                  .map((loc) => loc.location_id)
                                  .filter(
                                    (id) => !disableLocations.includes(id)
                                  )
                              );
                            } else {
                              setCheckedLocations([]);
                            }
                          }}
                          className="m-0 p-0"
                        />
                      </div>
                    )}
                    <div
                      className={`col-lg-${showLocationCheckBox ? "1" : "2"} title`}
                    >
                      Location
                    </div>
                    <div className="col-lg-1 title">Current Start Date</div>
                    <div className="col-lg-1 title">Current End Date</div>
                    <div className={`col-lg-1 title pr-lg-1`}>
                      Current Creatives
                    </div>
                    <div className="col-lg-1 title">Current Budget</div>
                    <div className="col-lg-2 title">
                      New Start Date
                      <ExtendHeaderTooltip label="New Start Date" />
                    </div>
                    <div className="col-lg-2 title">
                      New End Date
                      <ExtendHeaderTooltip label="New End Date" />
                    </div>
                    <div className="col-lg-2 title">New Budget</div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="col-12 pb-2 position-relative"
              id="extend-locations-list"
            >
              {!locationData?.length ? (
                <div className="d-flex-center w-100 py-5">
                  <RpAlert severity="error">
                    <Typography variant="caption">
                      No locations eligible for extension found for this
                      campaign
                    </Typography>
                  </RpAlert>
                </div>
              ) : (
                <RpVirtualList
                  items={locationData}
                  renderItem={(loc: ExtendLocation, index: number) => {
                    const locationId = loc.location_id;
                    const lc = values?.selectedLoc?.[locationId];

                    return (
                      <div
                        className="col-12 mb-1 mb-lg-auto p-2 px-lg-0 py-lg-0"
                        key={locationId}
                      >
                        <ExtendLocationForm
                          key={locationId}
                          location_id={locationId}
                          lc={lc}
                          loc={loc}
                          index={index}
                          campaignData={campaignData}
                          showLocationCheckBox={showLocationCheckBox}
                          checkedLocations={checkedLocations}
                          setCheckedLocations={setCheckedLocations}
                          disableLocations={disableLocations}
                        />
                      </div>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </EstimatedImpressionsVisibilityProvider>
  );
}

export default ExtendLocations;
